// ============================================================
//  AVIRAAPRO ERP — Smart API Client
//  File: src/utils/apiClient.ts
//  ✅ Drop-in replacement for `import axios from 'axios'`
//  ✅ Auto-adds Authorization header from localStorage
//  ✅ Queues POST/PUT/PATCH/DELETE when offline
//  ✅ Auto-refreshes JWT token when expired
//  ✅ Redirects to login on 401
// ============================================================

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { STORAGE_KEYS, getBackendUrl } from '../config';
import { enqueue } from './offlineQueue';
import { reportApiError } from './clientErrorLogger';
import {
  getOptionalSelectedCompanyId,
  hasExplicitCrossCompanyBodyTargets,
  hasExplicitCrossCompanyQueryTargets,
} from './companyContext';

// ── Create shared axios instance ──────────────────────────────
const apiClient: AxiosInstance = axios.create({
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});


// ── Helper: extract user_id from JWT token's `sub` claim ──────
// 19 May 2026 update: old sessions (logged in before Login.tsx started saving
// user_id to localStorage) would fail on endpoints that strictly require
// X-User-Id (e.g. 3-Way Match resolve/auto-resolve, Vendor Payment POST).
// Solution: decode the JWT we already have in localStorage and pull `sub`.
function getUserIdFromToken(): string | null {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 → atob → JSON
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    const payload = JSON.parse(atob(b64 + pad));
    const sub = payload?.sub;
    if (sub != null && /^\d+$/.test(String(sub))) {
      // Cache for next call (avoids re-decoding on every request)
      try { localStorage.setItem('user_id', String(sub)); } catch { /* ignore */ }
      return String(sub);
    }
  } catch { /* silent — bad token / atob failure */ }
  return null;
}

function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    return JSON.parse(atob(b64 + pad));
  } catch {
    return null;
  }
}

function isPositiveIntString(value: unknown): boolean {
  if (value == null || !/^\d+$/.test(String(value))) return false;
  return Number(String(value)) > 0;
}

function isFormDataPayload(value: unknown): boolean {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function removeContentTypeHeader(headers: any): void {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    headers.delete('content-type');
    return;
  }
  delete headers['Content-Type'];
  delete headers['content-type'];
}

function truthyClaim(value: unknown): boolean {
  return value === true || value === 1 || ['1', 'true', 'yes'].includes(String(value || '').toLowerCase());
}

function apiPathname(url: string): string {
  try {
    return new URL(url, 'http://aviraapro.local').pathname;
  } catch {
    return String(url || '').split(/[?#]/, 1)[0];
  }
}

function setCompanyHeader(headers: any, companyId: number, overwrite: boolean): void {
  if (!headers) return;
  const existing = typeof headers.get === 'function'
    ? (headers.get('X-Company-ID') || headers.get('x-company-id'))
    : (headers['X-Company-ID'] || headers['X-Company-Id'] || headers['x-company-id']);
  if (!overwrite && existing) return;
  if (typeof headers.delete === 'function') {
    headers.delete('X-Company-ID');
    headers.delete('X-Company-Id');
    headers.delete('x-company-id');
  } else {
    delete headers['X-Company-ID'];
    delete headers['X-Company-Id'];
    delete headers['x-company-id'];
  }
  if (typeof headers.set === 'function') headers.set('X-Company-ID', String(companyId));
  else headers['X-Company-ID'] = String(companyId);
}

function rewriteEmbeddedCompanyQuery(url: string, companyId: number): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) return url;
  const base = withoutHash.slice(0, queryIndex);
  const params = new URLSearchParams(withoutHash.slice(queryIndex + 1));
  if (!params.has('company_id')) return url;
  params.set('company_id', String(companyId));
  return `${base}?${params.toString()}${hash}`;
}

function rewriteCompanyParams(params: any, companyId: number): any {
  if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
    const next = new URLSearchParams(params);
    next.set('company_id', String(companyId));
    return next;
  }
  if (params == null) return { company_id: companyId };
  if (Object.prototype.toString.call(params) === '[object Object]') {
    return { ...params, company_id: companyId };
  }
  return params;
}

function rewriteNestedCompanyIds(value: any, companyId: number): [any, boolean] {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const [rewritten, itemChanged] = rewriteNestedCompanyIds(item, companyId);
      changed = changed || itemChanged;
      return rewritten;
    });
    return [changed ? next : value, changed];
  }
  if (Object.prototype.toString.call(value) !== '[object Object]') return [value, false];
  let changed = false;
  const next: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    if (key === 'company_id') {
      next[key] = companyId;
      changed = changed || item !== companyId;
      return;
    }
    const [rewritten, itemChanged] = rewriteNestedCompanyIds(item, companyId);
    next[key] = rewritten;
    changed = changed || itemChanged;
  });
  return [changed ? next : value, changed];
}

function rewriteCompanyBody(data: any, companyId: number): any {
  if (isFormDataPayload(data)) {
    if (data.has('company_id')) data.set('company_id', String(companyId));
    return data;
  }
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
    if (data.has('company_id')) data.set('company_id', String(companyId));
    return data;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      const [rewritten, changed] = rewriteNestedCompanyIds(parsed, companyId);
      return changed ? JSON.stringify(rewritten) : data;
    } catch {
      return data;
    }
  }
  return rewriteNestedCompanyIds(data, companyId)[0];
}

/**
 * Repair module-level/stale company_id values immediately before transport.
 * Cross-company administration routes keep their explicit target ids, while
 * the X-Company-ID header continues to identify the authenticated caller.
 */
export function applySelectedCompanyContext(
  config: AxiosRequestConfig,
  companyId: number,
): AxiosRequestConfig {
  if (!Number.isInteger(companyId) || companyId <= 0) return config;
  const requestUrl = String(config.url || '');
  if (!apiPathname(requestUrl).startsWith('/api/')) return config;

  const preserveQueryTargets = hasExplicitCrossCompanyQueryTargets(requestUrl);
  const preserveBodyTargets = hasExplicitCrossCompanyBodyTargets(requestUrl);
  if (!config.headers) config.headers = {} as any;
  setCompanyHeader(config.headers, companyId, true);
  if (!preserveQueryTargets) {
    config.url = rewriteEmbeddedCompanyQuery(requestUrl, companyId);
    config.params = rewriteCompanyParams(config.params, companyId);
  }
  if (!preserveBodyTargets) config.data = rewriteCompanyBody(config.data, companyId);
  return config;
}

function getStoredContextId(primaryKey: string, legacyKey: string, tokenClaim: string): string | null {
  try {
    const stored = localStorage.getItem(primaryKey) || localStorage.getItem(legacyKey);
    if (isPositiveIntString(stored)) return String(stored);
    const token = localStorage.getItem('token') || localStorage.getItem(STORAGE_KEYS.TOKEN);
    const claim = token ? decodeJwtPayload(token)?.[tokenClaim] : null;
    if (isPositiveIntString(claim)) {
      const value = String(claim);
      localStorage.setItem(primaryKey, value);
      localStorage.setItem(legacyKey, value);
      return value;
    }
  } catch { /* ignore */ }
  return null;
}

function syncSessionFromAccessToken(token: string | null | undefined): void {
  if (!token) return;
  const payload = decodeJwtPayload(token);
  if (!payload) return;
  try {
    if (isPositiveIntString(payload.sub)) {
      localStorage.setItem('user_id', String(payload.sub));
    }
    if (isPositiveIntString(payload.tenant_id)) {
      localStorage.setItem('tenant_id', String(payload.tenant_id));
      localStorage.setItem(STORAGE_KEYS.TENANT_ID, String(payload.tenant_id));
    }
    if (isPositiveIntString(payload.company_id)) {
      localStorage.setItem('company_id', String(payload.company_id));
      localStorage.setItem(STORAGE_KEYS.COMPANY_ID, String(payload.company_id));
    }
    if (payload.tenant_code) {
      localStorage.setItem('tenant_code', String(payload.tenant_code));
      localStorage.setItem(STORAGE_KEYS.TENANT_CODE, String(payload.tenant_code));
    }
    if (payload.tenant_name) {
      localStorage.setItem(STORAGE_KEYS.TENANT_NAME, String(payload.tenant_name));
    }
    if (payload.company_code) {
      localStorage.setItem(STORAGE_KEYS.COMPANY_CODE, String(payload.company_code));
    }
    if (payload.company_name) {
      localStorage.setItem(STORAGE_KEYS.COMPANY_NAME, String(payload.company_name));
    }
    if (payload.access_role) {
      localStorage.setItem('company_access_role', String(payload.access_role));
      localStorage.setItem('erp_access_role', String(payload.access_role));
      if (isPositiveIntString(payload.company_id)) {
        localStorage.setItem(`erp_company_access_role_${payload.company_id}`, String(payload.access_role));
      }
      try {
        const user = JSON.parse(localStorage.getItem('erp_user') || '{}');
        localStorage.setItem('erp_user', JSON.stringify({ ...user, access_role: payload.access_role }));
      } catch {
        localStorage.setItem('erp_user', JSON.stringify({ access_role: payload.access_role }));
      }
    }
    if (Array.isArray(payload.permissions)) {
      const permissions = payload.permissions.filter((item: unknown) => typeof item === 'string');
      const serialized = JSON.stringify(permissions);
      localStorage.setItem('permissions', serialized);
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, serialized);
      if (isPositiveIntString(payload.company_id)) {
        localStorage.setItem(`erp_company_permissions_${payload.company_id}`, serialized);
      }
    }
    if (payload.role) {
      localStorage.setItem('userRole', String(payload.role));
      if (isPositiveIntString(payload.company_id)) {
        localStorage.setItem(`erp_company_role_${payload.company_id}`, String(payload.role));
      }
    }
    if (payload.role_name) {
      localStorage.setItem('roleName', String(payload.role_name));
      if (isPositiveIntString(payload.company_id)) {
        localStorage.setItem(`erp_company_role_name_${payload.company_id}`, String(payload.role_name));
      }
    }
    if (payload.industry_type) localStorage.setItem(STORAGE_KEYS.INDUSTRY_TYPE, String(payload.industry_type));
  } catch { /* ignore */ }
}

let _accessRefreshPromise: Promise<string> | null = null;

export function resolveEffectiveCompanyId(
  storedCompanyId: unknown,
  tokenPayload: any,
): number | null {
  const tokenCompanyId = isPositiveIntString(tokenPayload?.company_id)
    ? Number(tokenPayload.company_id)
    : null;
  if (truthyClaim(tokenPayload?.company_context_locked) && tokenCompanyId) {
    return tokenCompanyId;
  }
  if (isPositiveIntString(storedCompanyId)) return Number(storedCompanyId);
  return tokenCompanyId;
}

function getSelectedCompanyForRefresh(token?: string | null): string | null {
  const payload = token ? decodeJwtPayload(token) : null;
  const selected = getOptionalSelectedCompanyId();
  const effective = resolveEffectiveCompanyId(selected, payload);
  return effective ? String(effective) : null;
}

async function refreshStoredAccessToken(refreshToken: string): Promise<string> {
  if (!_accessRefreshPromise) {
    const currentToken = localStorage.getItem('token') || localStorage.getItem(STORAGE_KEYS.TOKEN);
    const selectedCompanyId = getSelectedCompanyForRefresh(currentToken);
    _accessRefreshPromise = axios.post(`${getBackendUrl()}/api/auth/refresh`, {
      refresh_token: refreshToken,
      ...(selectedCompanyId ? { company_id: Number(selectedCompanyId) } : {}),
    }).then((res) => {
      const nextToken = String(res.data?.access_token || '');
      if (!nextToken) throw new Error('Refresh response missing access token');
      localStorage.setItem('token', nextToken);
      localStorage.setItem(STORAGE_KEYS.TOKEN, nextToken);
      syncSessionFromAccessToken(nextToken);
      return nextToken;
    }).finally(() => {
      _accessRefreshPromise = null;
    });
  }
  return _accessRefreshPromise;
}

function getTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    const payload = JSON.parse(atob(b64 + pad));
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isTokenExpiring(token: string, withinMs = 60_000): boolean {
  const expiresAt = getTokenExpiryMs(token);
  return expiresAt != null && expiresAt <= Date.now() + withinMs;
}

// ── Request Interceptor — dynamic baseURL + JWT token + X-User-Id ─────────
apiClient.interceptors.request.use(
  async (config) => {
    // Always read fresh backend URL (handles Electron URL changes)
    if (!config.baseURL && !config.url?.startsWith('http')) {
      config.baseURL = getBackendUrl();
    }
    if (isFormDataPayload(config.data)) {
      removeContentTypeHeader(config.headers);
    }
    let token = localStorage.getItem('token') || localStorage.getItem(STORAGE_KEYS.TOKEN);
    const refreshToken = localStorage.getItem('refresh_token') || localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const requestUrl = String(config.url || '');
    const isRefreshRequest = requestUrl.includes('/api/auth/refresh');

    const tokenPayload = token ? decodeJwtPayload(token) : null;
    const selectedCompanyId = getSelectedCompanyForRefresh(token);
    const tokenCompanyId = tokenPayload?.company_id;
    const companyContextChanged = Boolean(
      !truthyClaim(tokenPayload?.company_context_locked)
      && selectedCompanyId
      && String(tokenCompanyId || '') !== selectedCompanyId
    );
    if (token && (isTokenExpiring(token) || companyContextChanged) && !isRefreshRequest) {
      if (refreshToken) {
        try {
          token = await refreshStoredAccessToken(refreshToken);
        } catch (refreshError) {
          clearStoredSession();
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      } else {
        clearStoredSession();
        redirectToLogin();
        return Promise.reject({
          message: 'Session expire ho gaya hai. Please dobara login karein.',
          status: 401,
        });
      }
    }

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.headers && !config.headers['X-Tenant-ID'] && !config.headers['x-tenant-id']) {
      const tenantId = getStoredContextId(STORAGE_KEYS.TENANT_ID, 'tenant_id', 'tenant_id');
      if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
    }
    const effectiveCompanyId = getSelectedCompanyForRefresh(token);
    if (effectiveCompanyId) {
      applySelectedCompanyContext(config, Number(effectiveCompanyId));
    }
    // 19 May 2026: auto-attach X-User-Id header so backend actor_id is always populated.
    // Several action endpoints (Vendor Payment POST, AP Invoice POST, 3WM resolve etc.)
    // require this for JE/audit trail (created_by/resolved_by). Without it those
    // endpoints reject with 400.
    // Don't override if caller already set it explicitly.
    if (config.headers && !config.headers['X-User-Id'] && !config.headers['x-user-id']) {
      let uid = localStorage.getItem('user_id');
      // Fallback: if user_id missing (legacy session pre-Login.tsx fix), decode JWT
      if (!uid || !/^\d+$/.test(uid)) {
        uid = getUserIdFromToken();
      }
      if (uid && /^\d+$/.test(uid)) {
        config.headers['X-User-Id'] = uid;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// ── Response Interceptor — Handle errors smartly ─────────────
let _isRefreshing = false;
let _refreshQueue: Array<{ resolve: Function; reject: Function }> = [];

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem('username');
  localStorage.removeItem('user_id');
  localStorage.removeItem('permissions');
  localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem('userRole');
  localStorage.removeItem('roleName');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('tenant_code');
  localStorage.removeItem('company_id');
  localStorage.removeItem(STORAGE_KEYS.TENANT_ID);
  localStorage.removeItem(STORAGE_KEYS.TENANT_CODE);
  localStorage.removeItem(STORAGE_KEYS.TENANT_NAME);
  localStorage.removeItem(STORAGE_KEYS.COMPANY_ID);
  localStorage.removeItem(STORAGE_KEYS.COMPANY_CODE);
  localStorage.removeItem(STORAGE_KEYS.COMPANY_NAME);
  localStorage.removeItem(STORAGE_KEYS.INDUSTRY_TYPE);
  localStorage.removeItem('company_access_role');
  localStorage.removeItem('erp_access_role');
  localStorage.removeItem('erp_login_company_code');
  localStorage.removeItem('erp_enabled_modules');
  localStorage.removeItem('erp_enabled_routes');
  localStorage.removeItem('erp_disabled_routes');
  Object.keys(localStorage)
    .filter((key) => (
      key.startsWith('erp_enabled_modules_')
      || key.startsWith('erp_enabled_routes_')
      || key.startsWith('erp_disabled_routes_')
      || key.startsWith('erp_company_permissions_')
      || key.startsWith('erp_company_role_')
      || key.startsWith('erp_company_role_name_')
      || key.startsWith('erp_company_access_role_')
    ))
    .forEach((key) => localStorage.removeItem(key));
}

function redirectToLogin() {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

apiClient.interceptors.response.use(
  // ✅ Success — just pass through
  (response) => response,

  // ❌ Error handling
  async (error: AxiosError) => {
    const config = error.config as AxiosRequestConfig & { _retry?: boolean; _skipOffline?: boolean };

    // ── Network error / no response = OFFLINE ─────────────────
    if (!error.response) {
      const method = (config?.method || '').toUpperCase();
      const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

      if (isMutation && !config._skipOffline) {
        // Queue this request for later
        const url = (config.baseURL || '') + (config.url || '');
        const id = enqueue({
          method: method as any,
          url,
          data: config.data ? JSON.parse(config.data) : undefined,
          headers: config.headers as Record<string, string>,
          label: config.headers?.['X-Request-Label'] as string,
        });

        // Return a special response so caller knows it was queued
        return Promise.reject({
          isQueued: true,
          queueId: id,
          message: 'Offline — entry saved locally. Will sync when connected.',
          offline: true,
        });
      }

      reportApiError(error, { phase: 'network' });
      return Promise.reject({
        message: 'Server se connection nahi hai. Internet ya LAN check karo.',
        offline: true,
      });
    }

    // ── 401 Unauthorized ─────────────────────────────────────
    if (error.response.status === 401 && !config._retry) {
      const isTokenExpired = error.response.headers?.['x-token-expired'] === 'true';
      const refreshToken   = localStorage.getItem('refresh_token') || localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      const requestUrl = String(config.url || '');
      const isAuthRequest = requestUrl.includes('/api/users/login') || requestUrl.includes('/api/auth/refresh');

      // Electron/CORS may hide custom headers, so try refresh once on any protected 401.
      if (!isAuthRequest && refreshToken) {
        config._retry = true;

        if (_isRefreshing) {
          return new Promise((resolve, reject) => {
            _refreshQueue.push({ resolve, reject });
          }).then((token) => {
            if (config.headers) config.headers['Authorization'] = `Bearer ${token}`;
            return apiClient(config);
          });
        }

        _isRefreshing = true;

        try {
          const newToken = await refreshStoredAccessToken(refreshToken);

          _refreshQueue.forEach(({ resolve }) => resolve(newToken));
          _refreshQueue = [];
          _isRefreshing = false;

          if (config.headers) config.headers['Authorization'] = `Bearer ${newToken}`;
          return apiClient(config);

        } catch (refreshError) {
          _refreshQueue.forEach(({ reject }) => reject(refreshError));
          _refreshQueue = [];
          _isRefreshing = false;

          // Refresh failed — clear session and redirect to login
          clearStoredSession();
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      if (!isAuthRequest && (isTokenExpired || localStorage.getItem('token') || localStorage.getItem(STORAGE_KEYS.TOKEN))) {
        clearStoredSession();
        redirectToLogin();
        return Promise.reject({
          message: 'Session expire ho gaya hai. Please dobara login karein.',
          status: 401,
        });
      }

      // Token invalid (not expired) — just reject, don't auto-logout
      // This prevents mass-logout from permission-denied 401s
    }

    // ── 403 Forbidden ─────────────────────────────────────────
    if (error.response.status === 403) {
      const detail = (error.response.data as any)?.detail;
      // Structured business errors (e.g. CREDIT_HOLD_STRICT_NEEDS_APPROVER)
      // must reach the caller intact — don't replace with a generic message.
      if (detail && typeof detail === 'object' && (detail as any).code) {
        return Promise.reject(error);
      }
      return Promise.reject({
        message: typeof detail === 'string' ? detail : 'Permission denied — aapke paas yeh action karne ka permission nahi hai.',
        status: 403,
      });
    }

    // ── 429 Too Many Requests (rate limited) ──────────────────
    if (error.response.status === 429) {
      return Promise.reject({
        message: (error.response.data as any)?.detail || 'Too many requests. Thodi der baad try karo.',
        status: 429,
      });
    }

    // ── Other server errors — pass through ────────────────────
    if (error.response.status >= 500) {
      reportApiError(error, { phase: 'server' });
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// ── Helper: check if error means request was queued ───────────
export function isQueuedError(error: any): boolean {
  return error?.isQueued === true;
}

// ── Helper: check if error is offline ─────────────────────────
export function isOfflineError(error: any): boolean {
  return error?.offline === true;
}
